import React from 'react';
import { homesData } from '../data/mock';
import { MapPin, Users, CheckCircle, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

export const KibouorHomes = () => {
  return (
    <section id="homes" className="py-24 bg-gradient-to-b from-white to-amber-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-playfair text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            The Kibouor Homes
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Each home is a sanctuary where laughter echoes, stories are shared, and families are reborn.
          </p>
        </div>

        {/* Homes Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {homesData.map((home) => (
            <div
              key={home.id}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={home.image}
                  alt={home.name}
                  className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute top-4 right-4">
                  <Badge
                    variant={home.status === 'Active' ? 'default' : 'secondary'}
                    className={`${
                      home.status === 'Active'
                        ? 'bg-green-500 text-white'
                        : 'bg-amber-500 text-white'
                    } px-3 py-1 text-sm font-medium`}
                  >
                    {home.status === 'Active' ? (
                      <CheckCircle className="h-3 w-3 mr-1 inline" />
                    ) : (
                      <Clock className="h-3 w-3 mr-1 inline" />
                    )}
                    {home.status}
                  </Badge>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-playfair text-2xl font-bold text-gray-900 mb-2">
                  {home.name}
                </h3>
                <div className="flex items-center text-gray-600 mb-3">
                  <MapPin className="h-4 w-4 mr-2 text-amber-600" />
                  <span className="text-sm">{home.location}</span>
                </div>
                <div className="flex items-center text-gray-600 mb-4">
                  <Users className="h-4 w-4 mr-2 text-amber-600" />
                  <span className="text-sm">{home.capacity}</span>
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">{home.description}</p>
                <Button
                  variant="outline"
                  className="w-full border-amber-600 text-amber-600 hover:bg-amber-600 hover:text-white transition-colors duration-300"
                >
                  Sponsor This Home
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Expansion Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h3 className="font-playfair text-4xl font-bold text-gray-900 mb-4">
              Help Us Build More Homes
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              🌏 Expanding across Asia — bringing love and belonging to more communities.
            </p>
          </div>

          {/* Countries Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-12">
            {[
              { flag: '🇹🇭', name: 'Thailand', status: 'planned' },
              { flag: '🇻🇳', name: 'Vietnam', status: 'planned' },
              { flag: '🇲🇾', name: 'Malaysia', status: 'planned' },
              { flag: '🇮🇩', name: 'Indonesia', status: 'planned' },
              { flag: '🇵🇭', name: 'Philippines', status: 'active' },
              { flag: '🇮🇳', name: 'India', status: 'planned' }
            ].map((country, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group"
              >
                <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-300">
                  {country.flag}
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{country.name}</h4>
                <Badge
                  variant={country.status === 'active' ? 'default' : 'secondary'}
                  className={`${
                    country.status === 'active'
                      ? 'bg-green-500 text-white'
                      : 'bg-amber-100 text-amber-700'
                  } text-xs`}
                >
                  {country.status === 'active' ? 'Active' : 'Coming Soon'}
                </Badge>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="text-center bg-gradient-to-r from-amber-100 to-amber-50 rounded-2xl p-12 border border-amber-200">
            <h4 className="font-playfair text-3xl font-bold text-gray-900 mb-4">
              Your contribution creates spaces where hope lives and families flourish.
            </h4>
            <Button
              size="lg"
              className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg font-medium transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              onClick={() => document.getElementById('donate')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Donate to Expand Our Reach
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};